import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { InviteRole } from '@ghostscreen/contracts';
import { INVITE_ROLE_OPTIONS, parseInviteEmails } from './teamFormat';

type TeamsInviteCardProps = {
  sending: boolean;
  error: string | null;
  success: string | null;
  onSend: (input: { emails: string[]; role: InviteRole }) => void;
};

export function TeamsInviteCard({ sending, error, success, onSend }: TeamsInviteCardProps) {
  const { t } = useTranslation(['teams', 'common']);
  const [emailsRaw, setEmailsRaw] = useState('');
  const [role, setRole] = useState<InviteRole>('content_manager');

  useEffect(() => {
    if (success) setEmailsRaw('');
  }, [success]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const emails = parseInviteEmails(emailsRaw);
    if (emails.length === 0) return;
    onSend({ emails, role });
  };

  return (
    <section
      className="rounded-[14px] border border-line px-5 py-5"
      style={{ background: 'var(--gs-panel)' }}
    >
      <h2 className="m-0 text-base font-bold text-ink">{t('invite.title')}</h2>
      <p className="m-0 mt-1 text-xs text-muted">{t('invite.description')}</p>

      <form onSubmit={submit} className="mt-4 space-y-4">
        <div>
          <label
            className="mb-1.5 block text-xs font-semibold text-muted"
            htmlFor="invite-emails"
          >
            {t('invite.emailsLabel')}
          </label>
          <textarea
            id="invite-emails"
            value={emailsRaw}
            onChange={(event) => setEmailsRaw(event.target.value)}
            placeholder={t('invite.emailsPlaceholder')}
            rows={3}
            className="w-full resize-y rounded-[10px] border border-line px-3 py-2.5 text-sm text-ink outline-none focus:border-[var(--gs-accent)]"
            style={{ background: 'var(--gs-field)' }}
          />
        </div>

        <fieldset className="m-0 border-0 p-0">
          <legend className="mb-1.5 text-xs font-semibold text-muted">
            {t('invite.roleLegend')}
          </legend>
          <div className="flex flex-col gap-2">
            {INVITE_ROLE_OPTIONS.map((option) => {
              const active = role === option;
              return (
                <label
                  key={option}
                  className={`cursor-pointer rounded-[12px] border px-3 py-2.5 ${
                    active ? 'border-[var(--gs-accent)]' : 'border-line'
                  }`}
                  style={
                    active
                      ? { background: 'color-mix(in srgb, var(--gs-accent) 10%, transparent)' }
                      : { background: 'var(--gs-panel2)' }
                  }
                >
                  <span className="flex items-start gap-2">
                    <input
                      type="radio"
                      name="invite-role"
                      className="mt-1"
                      checked={active}
                      onChange={() => setRole(option)}
                    />
                    <span>
                      <span className="block text-sm font-semibold text-ink">
                        {t(`common:roles.${option}`)}
                      </span>
                      <span className="block text-xs text-muted">
                        {t(`invite.roleDescriptions.${option}`)}
                      </span>
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {error ? <p className="m-0 text-sm text-[var(--gs-bad)]">{error}</p> : null}
        {success ? <p className="m-0 text-sm text-[var(--gs-ok)]">{success}</p> : null}

        <button
          type="submit"
          disabled={sending || parseInviteEmails(emailsRaw).length === 0}
          className="w-full rounded-[10px] px-3.5 py-2.5 text-sm font-bold text-[var(--gs-accent-ink)] disabled:opacity-50"
          style={{ background: 'var(--gs-accent-solid)' }}
        >
          {sending ? t('invite.sending') : t('invite.send')}
        </button>
      </form>
    </section>
  );
}
